import React from 'react'

import { Input, Text, Toggle, Button } from 'components/base'
import {
  computeAuthdomainsErrors,
  AUTH_DOMAINS_NOT_EMPTY_ERROR,
} from './computeAuthDomainsErrors'

type JoinLayoutProps = {
  organizationDomain: string
  organizationMembersCanAutoJoin: boolean
  organizationAuthDomains: string | null
  organizationHasSSO: boolean
  updateOrganization: (params: any) => void
}

export default function JoinLayout({
  organizationDomain,
  organizationMembersCanAutoJoin,
  organizationAuthDomains,
  organizationHasSSO,
  updateOrganization,
}: JoinLayoutProps) {
  const [authDomainError, setAuthDomainError] = React.useState<string | null>(
    null
  )
  const [authDomains, setAuthDomains] = React.useState<string | null>(
    organizationAuthDomains
  )
  const [membersCanAutoJoin, setMembersCanAutoJoin] = React.useState<boolean>(
    organizationMembersCanAutoJoin
  )

  const canEditAuthDomains = organizationHasSSO || membersCanAutoJoin === true
  const hasChanged =
    authDomains !== organizationAuthDomains ||
    membersCanAutoJoin !== organizationMembersCanAutoJoin

  const onSave = React.useCallback(
    () => {
      const errors = computeAuthdomainsErrors(authDomains, membersCanAutoJoin)
      if (errors) {
        return setAuthDomainError(AUTH_DOMAINS_NOT_EMPTY_ERROR)
      }

      updateOrganization({
        authDomains,
        membersCanAutoJoin,
      })
    },
    [updateOrganization, authDomains, membersCanAutoJoin]
  )

  const onChangeAuthDomains = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
      .replace(/\s+/, '')
      .split(',')
      .join(',')
    setAuthDomainError(value ? '' : AUTH_DOMAINS_NOT_EMPTY_ERROR)
    setAuthDomains(value)
  }

  const onChangeMembersCanAutoJoin = () => {
    const value = !membersCanAutoJoin
    setMembersCanAutoJoin(value)
    setAuthDomains(value ? organizationAuthDomains : '')
  }

  return (
    <React.Fragment>
      <div className="width-full flexbox-align-center margin-bottom-small margin-top-large">
        <Text.Section className="margin-bottom-normal">Auto-join</Text.Section>
        <Toggle
          checked={membersCanAutoJoin}
          onChange={onChangeMembersCanAutoJoin}
        />
        <div className="flexbox flexbox-align-center">
          <Text.Corpus>
            {membersCanAutoJoin
              ? `Allow team members with the right email to join from ${organizationDomain}.slite.com`
              : `Auto joining is disabled for your team. Team members need to be invited manually.`}
          </Text.Corpus>
        </div>
      </div>
      <div className="margin-bottom-small margin-top-large">
        <Text.Section>Your auth domains</Text.Section>
        <Text.Corpus className="padding-top-small">
          This is a list of the domains your team accepts by default. It is used
          for Auto join but also SSO if you set it up. It is a sensitive setting
          and shouldn't be changed lightly.
        </Text.Corpus>
        <Text.Corpus className="padding-top-small">
          Common public domains (e.g.: gmail.com,hotmail.com,...) will be
          stripped out.
        </Text.Corpus>
        <div className="margin-top-normal">
          <Input
            name="authDomains"
            disabled={!canEditAuthDomains}
            value={authDomains}
            error={Boolean(authDomainError)}
            onChange={onChangeAuthDomains}
            placeholder="A list of comma separated domains. E.g.: acme.com,other-company.com"
          />
          {Boolean(authDomainError) && (
            <Text.Corpus
              className="padding-vertical-small"
              style={{ color: 'red' }}
            >
              {authDomainError}
            </Text.Corpus>
          )}
          {hasChanged && <Button.Primary onClick={onSave}>Save</Button.Primary>}
        </div>
      </div>
    </React.Fragment>
  )
}
